import { useIntl } from "react-intl";
import { Disclosure } from "@/components/design/Disclosure";
import { DegreeRequirementSelect } from "@/components/design/DegreeRequirementSelect";
import { PlusButton } from "@/components/design/PlusButton";
import {
  newCondition,
  type AiMode,
  type ConditionByTypename,
  type MatchCondition,
} from "../design-codec";
import type { MatchData } from "../match-design-types";
import { useRequirementsReferenceData } from "../use-requirements-reference-data";
import {
  RequirementControls,
  RowShell,
  TextValueInput,
} from "./row-primitives";

type EducationCondition = ConditionByTypename<"EducationCondition">;
type CertificateCondition = ConditionByTypename<"CertificateCondition">;
type QualificationCondition = EducationCondition | CertificateCondition;
type QualificationTypename = QualificationCondition["__typename"];

interface QualificationSectionCopy {
  title: { id: string; defaultMessage: string };
  add: { id: string; defaultMessage: string };
  placeholder: { id: string; defaultMessage: string };
}

const COPY: Record<QualificationTypename, QualificationSectionCopy> = {
  EducationCondition: {
    title: { id: "match.recruiting.educationTitle", defaultMessage: "Benötigte Ausbildung" },
    add: { id: "match.recruiting.addEducation", defaultMessage: "Ausbildung" },
    placeholder: { id: "match.recruiting.educationPlaceholder", defaultMessage: "z. B. Master Informatik oder kaufmännische Ausbildung" },
  },
  CertificateCondition: {
    title: { id: "match.recruiting.certificateTitle", defaultMessage: "Benötigte Zertifikate" },
    add: { id: "match.recruiting.addCertificate", defaultMessage: "Zertifikat" },
    placeholder: { id: "match.recruiting.certificatePlaceholder", defaultMessage: "z. B. AWS Solutions Architect" },
  },
};

export function RecruitingQualifications({
  data,
  update,
  readOnly = false,
  className = "",
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly?: boolean;
  className?: string;
}) {
  return (
    <>
      <QualificationSection
        typename="EducationCondition"
        data={data}
        update={update}
        readOnly={readOnly}
        className={className}
      />
      <QualificationSection
        typename="CertificateCondition"
        data={data}
        update={update}
        readOnly={readOnly}
        className={className}
      />
    </>
  );
}

function QualificationSection({
  typename,
  data,
  update,
  readOnly,
  className,
}: {
  typename: QualificationTypename;
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly: boolean;
  className: string;
}) {
  const intl = useIntl();
  const { degrees } = useRequirementsReferenceData();
  const copy = COPY[typename];
  const rows = data.conditions.filter(
    (condition): condition is QualificationCondition => condition.__typename === typename,
  );
  const aiOn = rows.some((condition) => condition.aiMode === "FULL_AI");

  function writeRows(next: QualificationCondition[]) {
    const others = data.conditions.filter((condition) => condition.__typename !== typename);
    update({ conditions: [...others, ...next] as MatchCondition[] });
  }

  function patch(index: number, values: Partial<QualificationCondition>) {
    const current = rows[index];
    if (!current) return;
    const next = rows.slice();
    next[index] = { ...current, ...values } as QualificationCondition;
    writeRows(next);
  }

  function remove(index: number) {
    const next = rows.slice();
    next.splice(index, 1);
    writeRows(next);
  }

  function add() {
    const aiMode: AiMode = aiOn ? "FULL_AI" : "NO_AI";
    const shared = {
      value: "",
      aiMode,
      operator: "EQ" as const,
      composition: { operator: "EXACT" as const, value: 1 },
    };
    const condition: QualificationCondition = typename === "EducationCondition"
      ? newCondition("EducationCondition", shared)
      : newCondition("CertificateCondition", shared);
    writeRows([...rows, condition]);
  }

  const displayRows = rows.map((condition, index) => ({ condition, index }));
  displayRows.sort(
    (a, b) => Number(b.condition.requirement === "MUST") - Number(a.condition.requirement === "MUST"),
  );

  if (rows.length === 0) {
    if (readOnly) return null;

    return (
      <div className="px-10 py-2" data-testid={`recruiting-${typename}-add`}>
        <PlusButton label={intl.formatMessage(copy.add)} onClick={add} />
      </div>
    );
  }

  return (
    <Disclosure
      className={`col-span-full ${className}`}
      title={intl.formatMessage(copy.title)}
      defaultOpen
    >
      <div className="space-y-2">
        {displayRows.map(({ condition, index }) => (
          <RowShell key={`${typename}-${index}`} onRemove={() => remove(index)} readOnly={readOnly}>
            <RequirementControls
              requirement={condition.requirement}
              onChange={(requirement) => patch(index, { requirement })}
              readOnly={readOnly}
            />
            {condition.__typename === "EducationCondition" ? (
              <DegreeRequirementSelect
                value={condition.value}
                onChange={(value) => patch(index, { value, operator: "EQ" })}
                options={degrees.map((degree) => ({
                  value: degree.label,
                  label: degree.label,
                }))}
                readOnly={readOnly}
              />
            ) : (
              <TextValueInput
                value={condition.value}
                onChange={(value) => patch(index, { value })}
                readOnly={readOnly}
                placeholder={intl.formatMessage(copy.placeholder)}
              />
            )}
          </RowShell>
        ))}
        {!readOnly ? (
          <div className="pt-1">
            <PlusButton label={intl.formatMessage(copy.add)} onClick={add} />
          </div>
        ) : null}
      </div>
    </Disclosure>
  );
}
